import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Image } from '@/components/Analysis/shared/Image';
import { PointOfInterest } from '@/__generated__/graphql';
import { ImagePreviewDialog } from './ImagePreviewDialog/ImagePreviewDialog';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { useSetAtom } from 'jotai';
import { poiNavigationActionsState, selectedPoiForPreviewState } from '../../state';
import {
  PreviewContainer,
  PreviewImage,
  SliderNavButton,
  DotsContainer,
  DotIndicator,
  EmptyPlaceholder,
} from './styles';

interface PreviewProps {
  data?: PointOfInterest;
  poiIndex?: number;
}

interface ImageItem {
  src: string;
  id: string;
  name?: string;
  type: 'primary' | 'attachment';
}

export const Preview = ({ data, poiIndex }: PreviewProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const setPoiNavigationActions = useSetAtom(poiNavigationActionsState);
  const setSelectedPoiForPreview = useSetAtom(selectedPoiForPreviewState);

  const images = useMemo(() => {
    const imageList: ImageItem[] = [];

    const primaryImageSrc = data?.screenshotPaths?.viewpoint
      ? getCloudfrontUrl(data.screenshotPaths.viewpoint)
      : null;

    if (primaryImageSrc) {
      imageList.push({
        src: primaryImageSrc,
        id: 'primary',
        name: data?.location?.name || 'Primary Image',
        type: 'primary'
      });
    }

    if (data?.files) {
      const attachmentImages = data.files
        .filter(file => file.resourcePath)
        .map(file => ({
          src: getCloudfrontUrl(file.resourcePath),
          id: file.resourcePath,
          name: file.name || 'Attachment',
          type: 'attachment' as const
        }));

      imageList.push(...attachmentImages);
    }

    return imageList;
  }, [data]);

  const hasPrimaryImage = data?.screenshotPaths?.viewpoint;
  const currentImage = images[currentSlideIndex] || images[0];

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlideIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlideIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
  };

  const handleDotClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlideIndex(index);
  };

  const handleOpen = () => {
    if (data && typeof poiIndex === 'number') {
      setPoiNavigationActions({ type: 'setIndex', payload: poiIndex });
      setSelectedPoiForPreview(data);
    }

    const currentPreviewImage = images[currentSlideIndex];
    if (currentPreviewImage) {
      setCurrentImageIndex(currentSlideIndex);
      setDialogOpen(true);
    } else {
      setCurrentImageIndex(0);
      setDialogOpen(true);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <PreviewContainer onClick={handleOpen}>
        <PreviewImage>
          {!hasPrimaryImage && images.length === 0 ? (
            <EmptyPlaceholder
              sx={{
                width: '25.6rem',
                height: '16.9rem',
              }}
            >
              Primary image not available
            </EmptyPlaceholder>
          ) : (
            <Image
              src={currentImage?.src || '/assets/reporting-spherical-view.png'}
              alt={currentImage?.name || "POI Preview"}
              imgSx={{
                width: '25.6rem',
                height: '16.9rem',
                objectFit: 'cover',
                borderRadius: '0.8rem',
                overflow: 'hidden',
                transition: 'opacity 0.3s ease-in-out',
              }}
            />
          )}
        </PreviewImage>

        {images.length > 1 && (
          <>
            <SliderNavButton
              className="slider-controls"
              onClick={handlePrevious}
              sx={{ left: '8px' }}
            >
              <ChevronLeft />
            </SliderNavButton>

            <SliderNavButton
              className="slider-controls"
              onClick={handleNext}
              sx={{ right: '8px' }}
            >
              <ChevronRight />
            </SliderNavButton>
          </>
        )}

        {images.length > 1 && (
          <DotsContainer>
            {images.map((_, index) => (
              <DotIndicator
                key={index}
                isActive={index === currentSlideIndex}
                onClick={(e) => handleDotClick(index, e)}
              />
            ))}
          </DotsContainer>
        )}
      </PreviewContainer>

      {dialogOpen && (
        <ImagePreviewDialog
          open={dialogOpen}
          onClose={handleClose}
          currentIndex={currentImageIndex}
          onChangeImage={setCurrentImageIndex}
          initialImageId={images[currentSlideIndex]?.id}
        />
      )}
    </>
  );
};
